import { ProviderZod } from 'src/domains/casino/types/types';
import { CountryCodeZod } from 'src/domains/layouts/config/countries';
import {
    AccountNavVariantType,
    ButtonVariantType,
    DefaultComponentVariantType,
    InputVariantType,
    NavMenuVariantType,
    NavigationVariantType,
    SwitchVariantType,
    TooltipVariantType,
} from 'src/domains/layouts/config/features/types';
import { CurrencyCodeZod } from 'src_common/common/amount/website-money/currency';
import { ContactPreferencesOptionSchema } from 'src_server/trpc/types/accountModel/changeAccountContactPreferencesNew';
import { ZodCatch, z } from 'zod';
import { zodToJsonSchema /*, ignoreOverride*/ } from 'zod-to-json-schema';

type NoUndefinedType<T> = T extends undefined ? never : T;

const describe = <T>(
    describe: string,
    defaultValue: NoUndefinedType<T>,
    validator: z.ZodType<T>
): ZodCatch<z.ZodDefault<z.ZodType<T>>> => {
    return (
        validator
            .describe(describe)
            .default(defaultValue)
            // .title
            .catch((error) => {
                console.info(`${describe} =>, catch error`, error);
                return defaultValue;
            })
    );
};

const GameWrapperZod = z.enum(['slider', 'slider-grid', 'square', 'rectangle', 'numeric-trending']);

const GameThumbnailSettingsZod = z.object({
    wrapperType: GameWrapperZod,
    showModalOnMobile: z.boolean(),
    showLikeAndInfo: z.boolean(),
    showRealTimeLobby: z.boolean(),
    minBetOnly: z.boolean(),
});

const GameThumbnailsSettingsZod = z.object({
    casino: GameThumbnailSettingsZod,
    'live-casino': GameThumbnailSettingsZod,
    virtuals: GameThumbnailSettingsZod,
});

const ImageOptimizationZod = z
    .discriminatedUnion('type', [
        // for prod
        z.object({
            type: z.literal('autoUrl'),
            usePublicGoogleUrls: z.boolean(),
        }),

        // for localhost & stage
        z.object({
            type: z.literal('baseUrl'),
            url: z.string(),
            usePublicGoogleUrls: z.boolean(),
        }),
    ])
    // disable optimization
    .nullable();

const SocketPragmaticZod = z.object({
    host: z.string(),
    casinoId: z.string(),
});

const FiltersSkinZod = z.enum(['primary', 'secondary', 'tertiary']);

const UsefulLinksKeysZod = z.enum([
    'about-us',
    'responsible-gambling',
    'safer-gambling',
    'faq',
    'privacy-policy',
    'terms-and-conditions',
    'cookie-policy',
    'betting-rules',
    'give-feedback',
    'affiliates',
    'partners',
    'customer-complaints-disputes',
    'careers',
    'bog-tcs',
]);

const FooterUsefulLinksItemZod = z.object({
    id: UsefulLinksKeysZod,
    order: z.number(),
    //TODO - przywrócić
    to: z.union([
        z.string(),
        z.object({
            account: z.literal('static'),
            static: z.string(),
        }),
    ]),
    target: z.enum(['_blank', '_self', '_parent', '_top']).nullish(),
    route: z.string().optional(),
    isStaticLink: z.boolean().optional(),
});

const FooterUsefulLinksZod = z.record(UsefulLinksKeysZod, FooterUsefulLinksItemZod);

const CollectionZod = z.enum(['casino', 'live-casino', 'virtuals']);
const CollectionWithBingoZod = z.enum([...CollectionZod.options, 'bingo']);

const HeaderLinkZod = z.enum([
    'sports',
    'in-play',
    'mcbookie-blog',
    'star-news',
    'dragonbet-news',

    'bingo',

    'casino',
    'live-casino',
    'virtuals',
    'football',
    'horse racing',
]);

export type HeaderLinkType = z.infer<typeof HeaderLinkZod>;

const SingleOGTagPropertyZod = z.object({
    property: z.string(),
    content: z.string(),
});

const SingleOGTagNameZod = z.object({
    name: z.string(),
    content: z.string(),
});

const MetaOpenGraphTagsZod = z.union([SingleOGTagPropertyZod, SingleOGTagNameZod]);

const CollectionWithBingo = z.enum([...CollectionZod.options, 'bingo']);

const MobileFooterLinksZod = z.enum([
    'cash-out',
    'football-european',
    'football',
    'home',
    'in-play',
    'offers',
    'promos',
    'racing',
    'sports-list',
    'sportsbook',
    'extra',
    'ak-big-prices',
    'ak-rewards',
    'optimove-inbox',
    ...CollectionWithBingo.exclude(['virtuals']).options,
]);

// SPORTSBOOK TYPES
export const ListOfSportsZod = z.enum([
    'americanfootball',
    'australianrules',
    'badminton',
    'baseball',
    'basketball',
    'boxing',
    'cricket',
    'cycling',
    'darts',
    'esports',
    'football',
    'formulaone',
    'gaelicfootball',
    'gaelichurling',
    'golf',
    'greyhoundracing',
    'handball',
    'horseracing',
    'horseracingantepost',
    'icehockey',
    'internationalhorseracing',
    'mixedmartialarts',
    'motorsport',
    'olympics',
    'politics',
    'rugbyleague',
    'rugbyunion',
    'snooker',
    'tabletennis',
    'tennis',
    'tennisdoubles',
    'tvspecials',
    'volleyball',
    'premier-league',
    'wrestling',
    'other',
]);

const PromotedEventRouteZod = z.enum([
    'aks-big-prices',
    'specials',
    'starevents',
    'welshevents',
    'promoted-events',
    '',
]);

const SeoMetaValueZod = z.object({
    title: z.string(),
    description: z.string(),
});

const SeoMetaZod = z.object({
    homepage: SeoMetaValueZod.nullable(),
    inplay: SeoMetaValueZod.nullable(),
    sports: SeoMetaValueZod.nullable(),
    'promoted-events': SeoMetaValueZod.nullable(),
    casino: SeoMetaValueZod.nullable(),
    'live-casino': SeoMetaValueZod.nullable(),
    virtuals: SeoMetaValueZod.nullable(),
});

const SeoCanonicalZod = z.string();

const SeoCanonicalMappingZod = z.record(z.string(), z.string());

const SeoMetaSportZod = z.record(ListOfSportsZod, SeoMetaValueZod);

const SeoSpecialSportsZod = z.array(z.record(z.string(), SeoMetaValueZod)).nullable();

const ContactPreferencesZod = z.enum(['email', 'sms', 'phone', 'whatsApp']);

export const PaysafePaymentProviderZod = z.object({
    type: z.literal('paysafe'),
    apiKey: z.string(),
    environment: z.enum(['TEST', 'LIVE']),
    cardAccounts: z.object({
        GBP: z.string(),
        EUR: z.string(),
    }),
    applePayEnabled: z.boolean(),
});

export const WorldpayCardProviderZod = z.object({
    type: z.literal('worldpay'),
    checkoutId: z.string(),
    environment: z.enum(['TEST', 'LIVE']),
});

export const WorldpayApplePayProviderZod = z.object({
    type: z.literal('worldpay'),
    merchantId: z.string(),
    initiativeContext: z.string(),
    domainName: z.string(),
});

const GooglePayProviderBase = z.object({
    type: z.literal('worldpay'),
    gatewayMerchantId: z.string(),
});

const TestGooglePayProviderZod = GooglePayProviderBase.extend({
    environment: z.literal('TEST'),
    googleMerchantId: z.never().optional(),
});

const ProductionGooglePayProviderZod = GooglePayProviderBase.extend({
    environment: z.literal('PRODUCTION'),
    googleMerchantId: z.string(),
});

export const GooglePayProviderZod = z.discriminatedUnion('environment', [
    TestGooglePayProviderZod,
    ProductionGooglePayProviderZod,
]);

const PaymentProvidersZod = z.union([
    z.object({
        type: z.literal('realex'),
    }),

    z.object({
        type: z.literal('praxis'),
    }),

    PaysafePaymentProviderZod,

    WorldpayCardProviderZod,

    z.object({
        type: z.null(),
    }),
]);

const YaspaOpenBankingPaymentProvider = z.object({
    type: z.literal('yaspa'),
    isEuroAllowed: z.boolean(),
    allowOnlyWithdraw: z.boolean(),
});

const OpenBankingProvidersZod = YaspaOpenBankingPaymentProvider.nullable();
export type OpenBankingProvidersType = z.infer<typeof OpenBankingProvidersZod>;

const MatchTypeZod = z.enum([
    'match-winner',
    'both-teams-to-score',
    'total-goals-over-under-2.5',
    'total-goals-over-under-1.5',
    'total-goals-over-under-0.5',
    'draw-no-bet',
    '1st-half-winner',
    'match-result-and-both-to-score',
]);

const HomepageWidget = z.union([
    z.literal('promoCarousel'),
    z.literal('quickLinkTray'),
    z.literal('heroWidget'),
    z.literal('xtremePush'),
    z.literal('racingWidget'),
    z.literal('inPlay'),
    z.literal('startingSoon'),
    z.literal('casinoGames'),
    z.literal('promotedEvents'),
    z.literal('virtualGames'),
    z.literal('priceChangeLegend'),
    z.literal('seoPagesContent'),
    z.literal('topEvents'),
]);

export type HomepageWidgetType = z.infer<typeof HomepageWidget>;

const IndexNowZod = z
    .object({
        host: z.string({
            required_error: 'IndexNow host is required',
            invalid_type_error: 'IndexNow host must be a string',
        }),
        key: z.string({
            required_error: 'IndexNow key is required',
            invalid_type_error: 'IndexNow key must be a string',
        }),
    })
    .nullable();

//..............................................................................

export const ConfigFeaturesZod = z.object({
    //..............................................................................
    //..............................................................................
    // CASINO CONFIG
    //..............................................................................
    //..............................................................................

    /**
     * set casino jack pot for each operator
     */
    casinoJackPot: describe('set casino jack pot for each operator', false, z.boolean()),

    /**
     * set casino mini games for each operator
     */
    casinoMiniGames: describe('set casino mini games for each operator', false, z.boolean()),

    /**
     * switch for games source between casino-store and website-cms
     */
    casinoStore: describe('switch for games source between casino-store and website-cms', false, z.boolean()),

    /**
     * turn on/off game status bar for slots
     */
    gameStatusBar: describe('turn on/off game status bar for slots', false, z.boolean()),

    /**
     * defines what features should be displayed on SingleGameExt
     */
    gameThumbnailSettings: describe(
        'defines what features should be displayed on SingleGameExt',
        {
            casino: {
                wrapperType: 'slider',
                showModalOnMobile: false,
                showLikeAndInfo: false,
                showRealTimeLobby: false,
                minBetOnly: false,
            },
            'live-casino': {
                wrapperType: 'slider',
                showModalOnMobile: false,
                showLikeAndInfo: false,
                showRealTimeLobby: false,
                minBetOnly: false,
            },
            virtuals: {
                wrapperType: 'slider',
                showModalOnMobile: false,
                showLikeAndInfo: false,
                showRealTimeLobby: false,
                minBetOnly: false,
            },
        },
        GameThumbnailsSettingsZod
    ),

    /**
     * turn on/off users name in continue playing headline
     */
    hasContinuePlayingName: describe('turn on/off users name in continue playing headline', false, z.boolean()),

    /**
     * turn on/off games on homepage
     */
    hasHeadlinePromo: describe('turn on/off games on homepage', false, z.boolean()),

    /**
     * turn on/off games on homepage
     */
    hasGamesOnHomepage: describe('turn on/off games on homepage', false, z.boolean()),

    /**
     * show / hide all casino game on Christmas day
     */
    hideAllCasinoGamesOnChristmas: describe('show / hide all casino game on Christmas day', false, z.boolean()),

    /**
     * turn on/off idMission socket and feature
     */
    idMission: describe('turn on/off idMission socket and feature', false, z.boolean()),
    /**
     * phone contact for identity verification issues
     */
    identityPhoneContact: describe('phone contact for identity verification issues', null, z.string().nullable()),
    /**
     * turn on/of image optimization with Cloudflare
     */
    imageOptimization: describe('turn on/of image optimization with Cloudflare', null, ImageOptimizationZod),

    /**
     * @description list of providers that require reload game after deposit
     */
    reloadGameAfterDepositForProviders: describe(
        'list of providers that require reload game after deposit',
        [],
        z.array(ProviderZod)
    ),

    /**
     * @description DGA for live casino
     */
    socketPragmatic: describe('DGA for live casino', null, SocketPragmaticZod.nullable()),

    //..............................................................................
    //..............................................................................
    // GROWTH CONFIG
    //..............................................................................
    //..............................................................................

    /**
     * @description New account drawer
     */
    accountDrawerNew: describe('New account drawer', false, z.boolean()),

    /**
     * @description Link for mobile app in store
     */
    appDownloadLink: describe(
        'Link for mobile app in store',
        {
            appStoreLink: null,
            googlePlayLink: null,
            showBanner: false,
        },
        z.object({
            appStoreLink: z.string().nullable(),
            googlePlayLink: z.string().nullable(),
            showBanner: z.boolean(),
        })
    ),
    /**
     * @description Hide apple pay for euro
     */
    applePayHiddenForEuro: describe('Hide apple pay for euro', false, z.boolean()),

    /**
     * @description Operator's cookiebot cookie consent banner id.
     */
    cookieBotId: describe("Operator's cookiebot cookie consent banner id", null, z.string().nullable()),

    /**
     * @description contact possibilities for Contact Us section in the main footer
     */
    contactDetails: describe(
        'contact possibilities for Contact Us section in the main footer',
        {
            phoneNumber: null,
            email: null,
            facebookLink: null,
            twitterLink: null,
            instagramLink: null,
        },
        z.object({
            phoneNumber: z.string().nullable(),
            email: z.string().nullable(),
            facebookLink: z.string().nullable(),
            twitterLink: z.string().nullable(),
            instagramLink: z.string().nullable(),
        })
    ),

    /**
     * @description temporary contact us list
     */
    contactUsTemporaryDefaultMock: describe(
        'temporary contact us list',
        [],
        z.array(
            z.object({
                contact_type: z.string(),
                contact_value: z.string(),
                display_order: z.number(),
                id: z.number(),
                is_active: z.boolean(),
                label: z.string().nullable().optional(),
                universe: z.string(),
            })
        )
    ),

    /**
     * @description set proper filters version
     */
    filtersVersion: describe(
        'set proper filters version',
        {
            betSlipTab: 'primary',
            casino: 'primary',
            football: 'primary',
            footballMain: 'primary',
            raceCardCompetitionTab: 'primary',
            raceCardMarketTab: 'primary',
            raceCardTime: 'primary',
            inPlay: 'primary',
            startingSoon: 'primary',
            eventsUpcoming: 'primary',
            eventsOnLater: 'primary',
            sportEvent: 'primary',
            competitionPage: 'primary',
            transactionHistory: 'primary',
            credits: 'primary',
        },
        z.object({
            /** @description Filters on Bets tab => Betslip | My bets */
            betSlipTab: FiltersSkinZod,
            casino: FiltersSkinZod,
            /** @description only football special filter skin */
            football: FiltersSkinZod,
            footballMain: FiltersSkinZod,
            raceCardCompetitionTab: FiltersSkinZod,
            raceCardMarketTab: FiltersSkinZod,
            raceCardTime: FiltersSkinZod,
            inPlay: FiltersSkinZod,
            startingSoon: FiltersSkinZod,
            eventsUpcoming: FiltersSkinZod,
            eventsOnLater: FiltersSkinZod,
            sportEvent: FiltersSkinZod,
            competitionPage: FiltersSkinZod,
            transactionHistory: FiltersSkinZod,
            credits: FiltersSkinZod,
        })
    ),

    /**
     * @description temporary footer copyright
     */
    footerCopyrightTemporaryDefaultMock: describe(
        'temporary footer copyright',
        {
            description_text: null,
            has_cookies: false,
            id: 0,
            licence_number: '',
            licence_url: null,
        },
        z.object({
            description_text: z.string().nullish(),
            has_cookies: z.boolean(),
            id: z.number(),
            licence_number: z.string(),
            licence_url: z.string().nullish(),
        })
    ),

    /**
     * @description Useful links section in footer
     */
    footerUsefulLinks: describe('Useful links section in footer', {}, FooterUsefulLinksZod),

    /**
     * @description Logos in footer for Safer Gambling
     */
    footerSaferGamblingLogos: describe(
        'Logos in footer for Safer Gambling',
        {
            ibas: true,
            ecogra: false,
            takeTimeToThink: true,
            gamblingTherapy: true,
            gamblingComissionRGLogo: true,
            netNanny: false,
        },
        z.object({
            ibas: z.boolean(),
            ecogra: z.boolean(),
            takeTimeToThink: z.boolean(),
            gamblingTherapy: z.boolean(),
            gamblingComissionRGLogo: z.boolean(),
            netNanny: z.boolean(),
        })
    ),

    /**
     * @description FreshDesk chat config
     */
    freshDeskChat: describe(
        'FreshDesk chat config',
        null,
        z
            .object({
                token: z.string(),
                host: z.string(),
                widgetUuid: z.string(),
            })
            .nullable()
    ),

    /**
     * @description fullstory id to track users on website
     */
    fullstory: describe('fullstory id to track users on website', null, z.string().nullable()),

    /**
     * @description google tag to google tag manager (google analytics and google optimize should be added through google tag manager panel) for Prod and non Prod env
     */
    gtm: describe(
        'google tag to google tag manager (google analytics and google optimize should be added through google tag manager panel) for Prod and non Prod env',
        '',
        z.string()
    ),

    /**
     * @description google optimize experiment id for dev (google optimize should be added through google tag manager panel)
     */
    googleExperimentIdDev: describe(
        'google optimize experiment id for dev (google optimize should be added through google tag manager panel)',
        '',
        z.string()
    ),

    /**
     * @description google optimize experiment id for production (google optimize should be added through google tag manager panel)
     */
    googleExperimentIdProd: describe(
        'google optimize experiment id for production (google optimize should be added through google tag manager panel)',
        '',
        z.string()
    ),

    /**
     * @description Genius Sports widget — base URL and product name (sports lists are in scoreboard.geniusGameTracker / scoreboard.geniusScoreboard)
     */
    geniusScoreboard: describe(
        'Genius Sports widget configuration with base URL and product name',
        null,
        z
            .object({
                baseUrl: z.string(),
                productName: z.string(),
            })
            .nullable()
    ),

    /**
     * @description enable Genius Multisport Scoreboard / Game Tracker iframes
     */
    geniusScoreboardsEnabled: describe(
        'enable Genius Multisport Scoreboard and Game Tracker widgets',
        false,
        z.boolean()
    ),

    /**
     * @description enable Genius Live Player for in-play event streams
     */
    geniusStreamingEnabled: describe('enable Genius Live Player streaming for in-play events', false, z.boolean()),

    /**
     * @description enable Genius design on the Home and InPlay pages
     */
    geniusHomeInPlayEnabled: describe('enable Genius design on the Home and InPlay pages', false, z.boolean()),

    /**
     * @description list of Genius sports
     */
    geniusSports: describe('list of Genius sports', [], z.array(z.string())),

    /**
     * @description has to collect info about signup on mobile app
     */
    sendInfoAboutSignUpToMobileApp: describe('has to collect info about signup on mobile app', false, z.boolean()),

    /**
     * @description turn on/off promo carousel view
     */
    hasCarouselPromo: describe('turn on/off promo carousel view', false, z.boolean()),

    /**
     * @description link general terms & conditions in footer copyright text
     */
    hasFooterCopyrightTCLink: describe('link general terms & conditions in footer copyright text', false, z.boolean()),

    /**
     * @description header links for universes
     */
    headerLinks: describe('header links for universes', [], z.array(HeaderLinkZod)),

    /**
     * @description headerMetaScale
     */
    headerMetaScale: describe(
        'headerMetaScale',
        'width=device-width, initial-scale=1, shrink-to-fit=no',
        z.enum([
            'width=device-width, initial-scale=1, shrink-to-fit=no',
            'width=device-width, initial-scale=1, user-scalable=no',
            'width=device-width, initial-scale=1, minimum-scale=1, shrink-to-fit=no, viewport-fit=cover',
            'width=device-width, initial-scale=1, user-scalable=no, shrink-to-fit=no',
            'width=device-width, initial-scale=1, user-scalable=no, shrink-to-fit=no, viewport-fit=cover',
        ])
    ),

    /**
     * @description headerMetaTitleName
     */
    headerMetaTitleName: describe('headerMetaTitleName', '', z.string()),

    /**
     * @description Facebook domain verification meta tag
     */
    headerMetaFacebookDomainVerification: describe(
        'Facebook domain verification meta tag',
        null,
        z.string().nullable()
    ),
    /**
     * @description Affelios site verification meta tag
     */
    headerMetaAffeliosSiteVerification: describe('Affelios site verification meta tag', null, z.string().nullable()),

    /**
     * @description link to the operator's blog / news section
     */
    headerSpecialLink: describe(
        "link to the operator's blog / news section",
        null,
        z.enum(['mcbookieblog', 'starsportsbet']).nullable()
    ),

    /**
     * @description sports which has to be hidden
     */
    hiddenSportsList: describe('sports which has to be hidden', [], z.array(z.string())),

    /**
     * @description flag to hide sports with no events on A-Z sports list
     */
    hideEmptySports: describe('flag to hide sports with no events on A-Z sports list', false, z.boolean()),

    /**
     * @description Hide All Sports filter on Homepage in section In Play
     */
    hideAllSportsFilterOnHomepageInPlaySection: describe(
        'Hide All Sports filter on Homepage in section In Play',
        true,
        z.boolean()
    ),

    /**
     * @description Show BetGlider widget on mobile (page and app).
     */
    isBetGliderActive: describe('Show BetGlider widget', false, z.boolean()),

    /**
     * @description add icon ios in headerMeta
     */
    isDarkUniverse: describe('add icon ios in headerMeta', false, z.boolean()),

    /** @description turn on new gambling commission regulations features and urls */
    isSaferGambling: describe(
        'turn on new gambling commission regulations features and urls',
        {
            available: false,
            hasTopBar: false,
            externalLink: null,
        },
        z.object({
            /** @description available - is UKGC available */
            available: z.boolean(),
            /** @description hasTopBar - is top bar available */
            hasTopBar: z.boolean(),
            /** @description externalLink - link to safer gambling page */
            externalLink: z.string().nullable(),
        })
    ),

    /**
     * @description Show no connection page when user has no internet connection
     */
    isNoConnectionPageVisible: describe(
        'Show no connection page when user has no internet connection',
        false,
        z.boolean()
    ),

    /** * @description Show boosted price on selection page */
    isBoostedPriceAvailable: describe('Show boosted price on selection', false, z.boolean()),

    /**
     * @description Show popup Reality Check for all pages excluded casino
     */
    isSeoPagesContentOn: describe('Show popup Reality Check for all pages excluded casino', false, z.boolean()),

    /**
     * @description Show popup Reality Check for all pages excluded casino
     */
    isRealityCheckAvailable: describe('Show popup Reality Check for all pages excluded casino', false, z.boolean()),

    /**
     * @description Index Now for Bing
     */
    indexNow: describe('Index Now', null, IndexNowZod),

    /**
     * @description Supported languages for the website
     */
    languagesAvailable: describe(
        'Supported languages for the website',
        [
            {
                langCode: 'en',
                langName: 'English',
            },
        ],
        z
            .array(
                z.object({
                    langCode: z.string(),
                    langName: z.string(),
                })
            )
            .nonempty({ message: 'At least one language must be available' })
    ),

    /**
     *  @description Set custom elements of layout
     */
    componentVariants: describe(
        'Custom layout options',
        {
            buttonVariant: ButtonVariantType.PRIMARY,
            inputVariant: InputVariantType.PRIMARY,
            tooltipVariant: TooltipVariantType.PRIMARY,
            nextOffSwitchVariant: SwitchVariantType.DARK,
            miniGamesVariant: DefaultComponentVariantType.PRIMARY,
            accountNavVariant: AccountNavVariantType.PRIMARY,
            casinoVariant: DefaultComponentVariantType.PRIMARY,
            casinoGameInfoVariant: DefaultComponentVariantType.PRIMARY,
            navMenuVariant: NavMenuVariantType.PRIMARY,
            navigationVariant: NavigationVariantType.PRIMARY,
            singleScoreVariant: DefaultComponentVariantType.PRIMARY,
            traderChatVariant: DefaultComponentVariantType.PRIMARY,
            nudgeVariant: DefaultComponentVariantType.PRIMARY,
            systemPagesVariant: DefaultComponentVariantType.PRIMARY,
            filtersVariant: DefaultComponentVariantType.PRIMARY,
            betslipVariant: DefaultComponentVariantType.PRIMARY,
            modalVariant: DefaultComponentVariantType.PRIMARY,
            raceCardVariant: DefaultComponentVariantType.PRIMARY,
            selectionVariant: DefaultComponentVariantType.PRIMARY,
            sportsbookVariant: DefaultComponentVariantType.PRIMARY,
            eventRowVariant: DefaultComponentVariantType.PRIMARY,
            scoreVariant: DefaultComponentVariantType.PRIMARY,
            collapseVariant: DefaultComponentVariantType.PRIMARY,
            accountSectionVariant: DefaultComponentVariantType.PRIMARY,
            selectVariant: DefaultComponentVariantType.PRIMARY,
            messagesVariant: DefaultComponentVariantType.PRIMARY,
            quickTrayLinkVariant: DefaultComponentVariantType.PRIMARY,
            layoutVariant: null,
        },
        z.object({
            buttonVariant: z.nativeEnum(ButtonVariantType),
            tooltipVariant: z.nativeEnum(TooltipVariantType),
            inputVariant: z.nativeEnum(InputVariantType),
            nextOffSwitchVariant: z.nativeEnum(SwitchVariantType),
            miniGamesVariant: z.nativeEnum(DefaultComponentVariantType).nullable(),
            accountNavVariant: z.nativeEnum(AccountNavVariantType).nullable(),
            casinoVariant: z.nativeEnum(DefaultComponentVariantType).nullable(),
            casinoGameInfoVariant: z.nativeEnum(DefaultComponentVariantType).nullable(),
            navMenuVariant: z.nativeEnum(NavMenuVariantType).nullable(),
            navigationVariant: z.nativeEnum(NavigationVariantType).nullable(),
            singleScoreVariant: z.nativeEnum(DefaultComponentVariantType),
            traderChatVariant: z.nativeEnum(DefaultComponentVariantType).nullable(),
            nudgeVariant: z.nativeEnum(DefaultComponentVariantType).nullable(),
            systemPagesVariant: z.nativeEnum(DefaultComponentVariantType).nullable(),
            filtersVariant: z.nativeEnum(DefaultComponentVariantType).nullable(),
            betslipVariant: z.nativeEnum(DefaultComponentVariantType).nullable(),
            modalVariant: z.nativeEnum(DefaultComponentVariantType).nullable(),
            raceCardVariant: z.nativeEnum(DefaultComponentVariantType).nullable(),
            selectionVariant: z.nativeEnum(DefaultComponentVariantType).nullable(),
            sportsbookVariant: z.nativeEnum(DefaultComponentVariantType).nullable(),
            eventRowVariant: z.nativeEnum(DefaultComponentVariantType).nullable(),
            scoreVariant: z.nativeEnum(DefaultComponentVariantType).nullable(),
            collapseVariant: z.nativeEnum(DefaultComponentVariantType).nullable(),
            accountSectionVariant: z.nativeEnum(DefaultComponentVariantType).nullable(),
            selectVariant: z.nativeEnum(DefaultComponentVariantType).nullable(),
            messagesVariant: z.nativeEnum(DefaultComponentVariantType).nullable(),
            quickTrayLinkVariant: z.nativeEnum(DefaultComponentVariantType).nullable(),
            layoutVariant: z.nativeEnum(DefaultComponentVariantType).nullable(),
        })
    ),

    /**
     * @description keeps routes and components of "Limits & Responsible Gambling"
     */
    limitsAndResponsibleGambling: describe(
        'keeps routes and components of "Limits & Responsible Gambling"',
        false,
        z.boolean()
    ),

    /**
     * @description show Manage Limits link at the top of footer Useful Links
     */
    manageLimitsUsefulLink: describe('show Manage Limits link at the top of footer Useful Links', false, z.boolean()),

    /**
     * @description Specifies whether live tracker should be displayed for football outrights
     */
    liveTrackerForFootballOutrights: describe(
        'Specifies whether live tracker should be displayed for football outrights',
        false,
        z.boolean()
    ),

    /**
     * @description apply meta open graph in html header
     */
    metaOpenGraph: describe('apply meta open graph in html header', null, z.array(MetaOpenGraphTagsZod).nullable()),

    /**
     * @description mobile footer links for all universes
     */
    mobileFooterLinks: describe('mobile footer links for all universes', [], z.array(MobileFooterLinksZod)),

    /**
     * @description sports to show in popular section. If needed it can be expanded for new sports
     */
    navigationPopular: describe(
        'sports to show in popular section. If needed it can be expanded for new sports',
        null,
        z.array(z.enum(['promotedevents', 'price-boost', ...ListOfSportsZod.options])).nullable()
    ),

    /**
     * @description show favourites section
     */
    navigationFavourites: describe('show favourites section', false, z.boolean()),

    /**
     * @description show recently viewed section
     */
    navigationRecentlyViewed: describe('show recently viewed section', false, z.boolean()),

    /**
     * @description set mobile navigation links different to the top nav/header ones or the same like top nav/header ones
     */
    navigationMobileLinks: describe(
        'set mobile navigation links different to the top nav/header ones or the same like top nav/header ones',
        'special-links',
        z.enum(['special-links', 'header-links'])
    ),

    /**
     * @description switch to render notifications
     */
    notificationsList: describe('switch to render notifications', false, z.boolean()),

    /**
     * @description Full name of each operator
     */
    operatorFullName: describe('Full name of each operator', '', z.string()),
    /**
     * @description Routes from external server with the same domain which are used for promotion
     */
    outsideAllowedRoutes: describe(
        'Routes from external server with the same domain which are used for promotion',
        [],
        z.array(z.string())
    ),
    optimizeHRCarousel: describe('optimized HR carousel enabled', false, z.boolean()),
    organizationSchema: describe(
        'Organization Schema',
        null,
        z
            .object({
                name: z.string(),
                url: z.string(),
                logo: z.string(),
                schema: z.record(z.string(), z.unknown()).nullable(),
            })
            .nullable()
    ),

    /**
     * @description add additional elements and styles when 404 error page is not a default one
     */
    page404Default: describe(
        'add additional elements and styles when 404 error page is not a default one',
        false,
        z.boolean()
    ),

    /**
     * Payment providers configuration
     *
     * @property {boolean} debitCard - Legacy payment form configuration (formerly usePaymentFormForUniverse)
     * @property {boolean} openBanking - Legacy open banking integration (formerly useOpenBanking)
     * @property {Object} applePay - Apple Pay integration via Paysafe for multi-deposit journey
     *                                Note: applied only when isNewPaymentJourneyEnabled is true. Temporary duplication for operators using Paysafe card deposits because they will switch to realex for debit card
     * @property {boolean} isNewPaymentJourneyEnabled - Toggle for enabling/disabling the new payment journey
     */

    paymentProviders: describe(
        'Payment providers configuration',
        {
            debitCard: { type: 'realex' },
            openBanking: null,
            applePay: null,
            googlePay: null,
            isNewPaymentJourneyEnabled: false,
        },
        z.object({
            debitCard: PaymentProvidersZod,
            openBanking: OpenBankingProvidersZod,
            applePay: z.union([PaysafePaymentProviderZod, WorldpayApplePayProviderZod]).nullable(),
            googlePay: GooglePayProviderZod.nullable(),
            isNewPaymentJourneyEnabled: z.boolean(),
        })
    ),
    /**
     * @description show Problem Gambling link in responsible gambling section
     */
    problemGambling: describe('show Problem Gambling link in responsible gambling section', false, z.boolean()),
    /**
     * @description Show promoted events in LHS menu and main page and set route
     */
    promotedEvents: describe(
        'Show promoted events in LHS menu and main page and set route',
        {
            label: '',
            route: '',
        },
        z.object({
            label: z.string(),
            route: PromotedEventRouteZod,
        })
    ),

    /**
     * @description seo canonical
     * @see {@link https://playbookengineering.atlassian.net/wiki/spaces/Texas/pages/33699299330/SEO+meta Documentation}
     */
    seoCanonical: describe('seo canonical', null, SeoCanonicalZod.nullable()),

    /**
     * @description custom URL mappings for canonical tags
     * @see {@link https://playbookengineering.atlassian.net/wiki/spaces/Texas/pages/33699299330/SEO+meta Documentation}
     */
    seoCanonicalMapping: describe('seo canonical tag mappings', null, SeoCanonicalMappingZod.nullable()),

    /**
     * @description seo headers, title, description
     * @see {@link https://playbookengineering.atlassian.net/wiki/spaces/Texas/pages/33699299330/SEO+meta Documentation}
     */
    seoMeta: describe('seo headers, title, description', null, SeoMetaZod.nullable()),

    /**
     * @description seo headers, title, description for sport
     * @see {@link https://playbookengineering.atlassian.net/wiki/spaces/Texas/pages/33699299330/SEO+meta Documentation}
     */
    seoMetaSport: describe('seo headers, title, description for sport', null, SeoMetaSportZod.nullable()),

    /**
     * @description seo headers, title, description for special sports
     * @see {@link https://playbookengineering.atlassian.net/wiki/spaces/Texas/pages/33699299330/SEO+meta Documentation}
     */
    seoMetaSpecialSports: describe(
        'seo headers, title, description for special sports',
        null,
        SeoSpecialSportsZod.nullable()
    ),

    /**
     * @description set scoreboard mode:
     * basic: only score and time
     * standard: old scoreboard with animation
     * liveTracker: new live tracker with animation
     * lSport: LSport tennis and golf scoreboard
     * geniusGameTracker / geniusScoreboard: Genius Sports iframes (requires top-level geniusScoreboard config)
     */
    scoreboard: describe(
        'set scoreboard mode: basic: only score and time, standard: old scoreboard with animation, liveTracker: new live tracker with animation, lSport: LSport tennis and golf scoreboard, geniusGameTracker / geniusScoreboard: Genius Sports iframes',
        {
            basic: null,
            standard: null,
            liveTracker: null,
            lSport: null,
            imgArena: null,
            geniusGameTracker: null,
            geniusScoreboard: null,
        },
        z.object({
            basic: z.array(ListOfSportsZod).nullable(),
            standard: z.array(ListOfSportsZod).nullable(),
            liveTracker: z.array(ListOfSportsZod).nullable(),
            /**
             * @description LSport now is Statscore. For LSport eventId we have to add prefix m:eventId => example: "m:301832"
             */
            lSport: z.array(ListOfSportsZod).nullable(),
            imgArena: z.enum(['golf']).nullable(),
            /**
             * @description Genius Multisport Game Tracker — sports for which the game tracker iframe is shown (requires geniusScoreboard config)
             */
            geniusGameTracker: z.array(ListOfSportsZod).nullable(),
            /**
             * @description Genius Multisport Scoreboard — sports for which the scoreboard iframe is shown (requires geniusScoreboard config)
             */
            geniusScoreboard: z.array(ListOfSportsZod).nullable(),
        })
    ),

    /**
     * @description header meta css
     */
    siteVerificationMetaMap: describe(
        'header meta css',
        null,
        z
            .object({
                name: z.string(),
                content: z.string(),
            })
            .nullable()
    ),
    /**
     * @description Add social widget on RHS menu
     */
    socialWidgets: describe(
        'Add social widget on RHS menu',
        {
            twitter: null,
        },
        z.object({
            twitter: z
                .object({
                    url: z.string(),
                    theme: z.enum(['light', 'dark']),
                })
                .nullable(),
        })
    ),

    /**
     * @description groupId (for the whole platform) from Statscore
     */
    statsCoreGroupId: describe(
        'groupId (for the whole platform) from Statscore',
        {
            preMatchPro: '',
            liveMatchPro: '',
        },
        z.object({
            /**
             * @description preMatchPro - pre match widget
             */
            preMatchPro: z.string().describe('preMatchPro - pre match widget'),
            /**
             * @description liveMatchPro - live match widget
             */
            liveMatchPro: z.string().describe('liveMatchPro - live match widget'),
        })
    ),
    /**
     * @description option to download T&C
     */
    termsAndConditionsDownloadLink: describe('option to download T&C', false, z.boolean()),

    /**
     * @description trader chat
     */
    traderChat: describe(
        'trader chat',
        {
            isOn: false,
            isBubbleChatButton: false,
        },
        z.object({
            /**
             * @description turn on/off trader chat
             */
            isOn: z.boolean().describe('turn on/off trader chat'),
            /**
             * @description turn on/off floating icon trader chat
             */
            isBubbleChatButton: z.boolean().describe('turn on/off floating icon trader chat'),
        })
    ),
    /**
     * @description turn on component for unSupportedBrowsers
     */
    unSupportedBrowsers: describe('turn on component for unSupportedBrowsers', false, z.boolean()),
    /**
     * @description set logo for unSupportedBrowsers component
     */
    unSupportedBrowsersLogo: describe(
        'set logo for unSupportedBrowsers component',
        'StarsportsLogoIconWrapper',
        z.enum(['StarsportsLogoIconWrapper', 'McBookieLogoWrapper'])
    ),
    //..............................................................................
    //..............................................................................
    // PLAYERS CONFIG
    //..............................................................................
    //..............................................................................

    /**
     * @description account for help contact
     */
    accountHelperMail: describe('account for help contact', '', z.string()),
    /**
     * @description prefix for account Id
     */
    accountIdPrefix: describe('prefix for account Id', '', z.string()),
    /**
     * @description accounts bonus engine
     */
    bonusEngine: describe('accounts bonus engine', false, z.boolean()),
    /**
     * @description defines published casino collections
     */
    casinoPublishedCollections: describe('defines published casino collections', [], z.array(CollectionWithBingoZod)),

    /**
     * @description defines contact preferences options
     */
    contactPreferences: describe('defines contact preferences options', [], z.array(ContactPreferencesZod)),
    /**
     * @description turn on/off closure mode for the operator
     */
    closureMode: describe('turn on/off closure mode for the operator - whole site is closed', false, z.boolean()),

    /**
     * @description turn on/off closure mode for the operator
     */
    closureModeWithLogin: describe(
        'turn on/off closure mode for the operator - leave login available',
        false,
        z.boolean()
    ),

    /**
     * @description turn on/off closure mode for the operator
     */
    closureModeSignupOnly: describe('turn on/off closure mode for the operator only for signup', false, z.boolean()),

    /**
     * @description full-screen Midnite migration experience (replaces legacy closure overlay when closure mode is on)
     */
    midnightMigrationScreen: describe(
        'show Midnite migration screen instead of standard closure overlay',
        false,
        z.boolean()
    ),

    /**
     * @description defines contact preferences categories
     */
    contactPreferencesCategories: describe(
        'defines contact preferences categories',
        [],
        z.array(ContactPreferencesOptionSchema)
    ),

    /**
     * @description only default country in signup
     */
    countryOnlyUKDefault: describe('only default country in signup', false, z.boolean()),
    /**
     * @description default country in sign up
     */
    countrySignUpDefault: describe('default country in sign up', null, CountryCodeZod.nullable()),
    /**
     * @description default currency
     */
    currencyDefault: describe('default currency', 'GBP', CurrencyCodeZod),
    /**
     * @description have default currency selected in signup
     */
    currencyDefaultSelectedInSignup: describe('have default currency selected in signup', false, z.boolean()),
    /**
     * @description only default currency in signup
     */
    currencyOnlyDefault: describe('only default currency in signup', false, z.boolean()),
    /**
     * @description list of currencies in signup
     */
    currencySignUpList: describe('list of currencies in signup', [], z.array(CurrencyCodeZod)),
    /**
     * @description disable customer funds protection tab
     */
    customerFundsProtection: describe('disable customer funds protection tab', false, z.boolean()),

    /**
     * @description specify how many places after comma we want to have in value
     */
    decimalLength: describe('specify how many places after comma we want to have in value', 0, z.number()),

    /**
     * @description specify how we will display stake input placeholder text
     */
    decimalFormatTextForPlaceholder: describe(
        'specify how we will display stake input placeholder text',
        '',
        z.string()
    ),
    /**
     * @description BetWright-style deposit limits: Deposit Limits and Net Deposit Limits stacked, without a toggle
     */
    depositLimitsStackedView: describe(
        'stack Deposit Limits and Net Deposit Limits instead of switching them with a toggle',
        false,
        z.boolean()
    ),
    /**
     * @description when a user has two NET limits for the same window, prefer calendar
     */
    depositLimitsPreferCalendarNetOnDuplicate: describe(
        'when a user has two NET limits for the same window, prefer calendar',
        false,
        z.boolean()
    ),

    /**
     * @description turn off deposits for the leaving operator
     */
    depositsTurnOffForLeavingOperator: describe(
        'turn off deposits for the leaving operator',
        { enabled: false },
        z.union([
            z.object({ enabled: z.literal(false) }),
            z.object({ enabled: z.literal(true), operatorName: z.string(), date: z.string() }),
        ])
    ),

    /**
     * @description detect login after signup in mobile app
     */
    detectLoginInMobileApp: describe('detect login after signup in mobile app', false, z.boolean()),

    /**
     * @description detect login after signup in mobile app
     */
    defaultPromoCodeFieldOpen: describe('signup default promo code field open', false, z.boolean()),

    /**
     * @description remove countries from signup dropdown
     */
    excludedCountriesFromSignUp: describe('remove countries from signup dropdown', [], z.array(z.string())),

    /**
     * @description email to kyc warning
     */
    emailKycWarning: describe('email to kyc warning', '', z.string()),

    /**
     * @description input label type in forgot password form
     */
    forgotPasswordInputLabel: describe(
        'input label type in forgot password form',
        'default',
        z.enum(['default', 'mcbookie'])
    ),

    /**
     * @description input type in forgot password form
     */
    forgotPasswordInputType: describe('input type in forgot password form', 'email', z.enum(['email', 'text'])),

    /**
     * @description detailed failed verification page in sign up journey
     */
    failedVerificationDetailed: describe('detailed failed verification page in sign up journey', false, z.boolean()),

    /**
     * @description turn on/off faq section
     */
    faqSection: describe(
        'turn on/off faq section',
        {
            isOnForAuthorized: false,
            isOnForNonAuthorized: false,
        },
        z.object({
            isOnForAuthorized: z.boolean(),
            isOnForNonAuthorized: z.boolean(),
        })
    ),

    /**
     * @description turn on/off able to cashout
     */
    hasCashoutEnabled: describe('turn on/off able to cashout', false, z.boolean()),

    /**
     * @description turn on/off section with help in account
     */
    hasNeedHelpAccountSection: describe('turn on/off section with help in account', false, z.boolean()),

    /**
     * @description turn on/off able to change address
     */
    hasUserAddressChangeEnabled: describe('turn on/off able to change address', false, z.boolean()),

    /**
     * @description turn on/off able to change contact number
     */
    hasUserContactNumberChangeEnabled: describe('turn on/off able to change contact number', false, z.boolean()),

    /**
     * @description turn on/off able to change email address
     */
    hasUserEmailChangeEnabled: describe('turn on/off able to change email address', false, z.boolean()),

    /**
     * @description enable the account verification via SMS
     */
    isVerifyAccountWithSMSAvailable: describe('enable the account verification via SMS', false, z.boolean()),

    /**
     * @description enable intercome chat
     */
    intercomChat: describe('enable intercome chat', false, z.boolean()),
    /**
     * @description App ID for intercom chat
     */
    intercomChatAppId: describe('App ID for intercom chat', '', z.string()),
    /**
     * @description Intercom chat region
     */
    intercomChatRegion: describe('Intercom chat region', 'eu', z.enum(['us', 'eu', 'ap'])),
    /**
     * @description URL for Intercom Chat API base
     */
    intercomChatApiBase: describe('URL for Intercom Chat API base', '', z.string()),

    /**
     * @description - autologin after reset password success
     */
    loginOnResetPasswordSuccess: describe('autologin after reset password success', false, z.boolean()),

    /**
     * @description send session ping only on user activity
     */
    sessionActivityBasedPing: describe('send session ping only on user activity', false, z.boolean()),

    /**
     * @deprecated - license
     */
    licenseName: describe('license', '', z.string()),

    /**
     * @description - minimum deposit amount
     */
    minDepositAmount: describe('minimum deposit amount', '0', z.string()),
    /**
     * @description - minimum withdraw amount
     */
    minWithdrawAmount: describe('minimum withdraw amount', '0', z.string()),

    /**
     * @description - show help and support section
     */
    navigationHelpSupport: describe('show help and support section', false, z.boolean()),

    /**
     * @description only countries for signup dropdown
     */
    onlyCountriesForSignUp: describe('only countries for signup dropdown', [], z.array(z.string())),

    /**
     * @description enable Optimove service worker
     */
    optimoveWorkerEnabled: describe('enable Optimove service worker', false, z.boolean()),

    /**
     * @description Optimove badge host element ID (e.g. 'optimove-badge-host'). Currently all set to "optimove-badge-host" but operators can diverge if they wish.
     */
    optimoveBadgeHostId: describe('Optimove badge host element ID', null, z.string().nullable()),

    /**
     * @description Emit shared Optimove inbox styles using operator theme tokens
     */
    optimoveInboxCustomised: describe('Emit shared Optimove inbox styles', false, z.boolean()),

    /**
     * @description Show Optimove inbox for visitors (non-authenticated users)
     */
    optimoveShowInboxForVisitors: describe('Show Optimove inbox for visitors', true, z.boolean()),

    /**
     * @description Optimove brand ID, used when reporting consent/unsubscribe events to the Optimove SDK
     */
    optimoveBrandId: describe('Optimove brand ID', null, z.string().nullable()),

    /**
     * @description restrict postcode field validation to UK postcode format only
     */
    postcodeUkOnly: describe('restrict postcode field validation to UK postcode format only', false, z.boolean()),

    /**
     * @description - show default prefix and flag for personal details in account
     */
    prefixAndFlagDefaultForPersonalDetails: describe(
        'show default prefix and flag for personal details in account',
        null,
        z
            .object({
                id: z.string(),
                prefix: z.string(),
                label: z.string(),
            })
            .nullable()
    ),

    /**
     * @description - show default prefix for personal details in account
     */
    prefixAndPhoneNumberDefault: describe(
        'show default prefix for personal details in account',
        {},
        z.record(z.string(), z.string())
    ),

    /**
     * @description - show dropdown for mobile prefixes in registration and settings
     */
    prefixMultiPhoneNumber: describe(
        'show dropdown for mobile prefixes in registration and settings',
        false,
        z.boolean()
    ),

    /**
     * @description - priority prefixes that are in the front of phone number prefixes list
     */
    priorityPrefixes: describe(
        'priority prefixes that are in the front of phone number prefixes list',
        [],
        z.array(z.string())
    ),

    /**
     * @description - Show Proof of Funds modals
     */
    pofModals: describe('Show Proof of Funds modals', false, z.boolean()),

    /**
     * @description - hide stag from the url on startup
     */
    hideStagOnStartup: describe('hide stag from the url on startup', false, z.boolean()),

    /**
     * @description - reset password form
     */
    resetPasswordForm: describe(
        'reset password form',
        'ResetPasswordForm',
        z.enum(['ResetPasswordForm', 'McBookieValidateAccountForm'])
    ),

    /**
     * @description - set ringFencedFunds by default during signup
     */
    ringFencedFundsSetByDefault: describe('set ringFencedFunds by default during signup', false, z.boolean()),

    /**
     * @description - popup for users after adding new feature to operators - only when feature was created
     */

    rollingNetDepositLimitFirstTimePopupType: describe(
        'popup for users after adding new feature to operators - only when feature was created',
        'primary',
        z.enum(['primary', 'secondary', 'tertiary'])
    ),

    /**
     * @description - show KycStatus
     */
    showKycStatus: describe('show KycStatus', false, z.boolean()),

    /**
     * @description - show Kyc warning in deposit tab
     */
    showKycDepositWaring: describe('show Kyc warning in deposit tab', false, z.boolean()),

    /**
     * @description - signUp disable button YourDetails
     */
    signUpDisableButtonYourDetails: describe('signUp disable button YourDetails', false, z.boolean()),

    /**
     * @description - signUpFooterMail
     */
    signUpFooterMail: describe('signUpFooterMail', '', z.string()),

    /**
     * @description - signUpShowPromoCode
     */
    signUpShowPromoCode: describe('signUpShowPromoCode', false, z.boolean()),

    /**
     * @description - turn on/off searching address by postcode in signup procedure
     */
    signUpAddressSearchByPostCode: describe(
        'turn on/off searching address by postcode in signup procedure',
        false,
        z.boolean()
    ),

    /**
     * @description - signup checkbox for Terms & Conditions
     */
    signUpCheckboxTC: describe('signup checkbox for Terms & Conditions', false, z.boolean()),

    /**
     * @description - pass registration without checking age verification
     */
    signUpOmitAgeVerification: describe('pass registration without checking age verification', false, z.boolean()),

    /**
     * @description - show second line option to choose during signup
     */
    signUpSecondAddressLine: describe('show second line option to choose during signup', false, z.boolean()),

    /**
     * @description - show title option to choose during signup
     */
    signUpTitle: describe('show title option to choose during signup', false, z.boolean()),

    /**
     * @description - show signup privacy policy link on signup
     */
    signupPrivacyPolicyLink: describe('show signup privacy policy link on signup', false, z.boolean()),

    /**
     * @description - show signup terms & conditions link on signup
     */
    signupTermsAndConditionsLink: describe('show signup terms & conditions link on signup', false, z.boolean()),

    /**
     * @description - turn off signup for the leaving operator
     */
    signupTurnOffForLeavingOperator: describe(
        'turn off signup for the leaving operator',
        { enabled: false },
        z.union([
            z.object({ enabled: z.literal(false) }),
            z.object({ enabled: z.literal(true), operatorName: z.string(), date: z.string() }),
        ])
    ),
    /**
     * @description - has additional note about empty limits
     */
    signupAdditionalNoteOnLimits: describe('has additional note about empty limits', false, z.boolean()),

    /**
     * @description Show GAMSTOP previously positive blocking popup
     */
    gamstopPreviouslyPositivePopup: describe('Show GAMSTOP previously positive blocking popup', false, z.boolean()),

    /**
     * @description - show the SRG Questionnaire form modal
     */
    srgQuestionnaire: describe('show the SRG Questionnaire form modal', false, z.boolean()),

    /**
     * @description - show the SRG Questionnaire form modal failed version
     */
    srgQuestionnaireFailedVersion: describe(
        'show the SRG Questionnaire form modal failed version',
        'primary',
        z.enum(['primary', 'secondary'])
    ),

    /**
     * @description - -----FeaturesFlag => should be always FALSE-----
     */
    timeoutToTestSignupFlow: describe('-----FeaturesFlag => should be always FALSE-----', false, z.boolean()),

    /**
     * @description - user can change Reality Check duration
     */
    userCanChangeRealityCheck: describe('user can change Reality Check duration', false, z.boolean()),

    /**
     * @description - Change to zip code name and signup validate dropdowns
     */
    yaspaMobileAppValidVersion: describe(
        'Version from which Yaspa is valid on mobile apps',
        null,
        z.string().nullable()
    ),

    /**
     * @description - Change to zip code name and signup validate dropdowns
     */
    zipCodeAndCountryCheck: describe('Change to zip code name and signup validate dropdowns', false, z.boolean()),

    //..............................................................................
    //..............................................................................
    // SPORTSBOOK CONFIG
    //..............................................................................
    //..............................................................................

    /**
     * @description - set checkbox colors for Build Racecard
     */
    buildRacecardCheckboxColorMode: describe(
        'set checkbox colors for Build Racecard',
        'light',
        z.enum(['dark', 'light'])
    ),

    /**
     * @description - coupon for environment
     */
    couponForEnv: describe(
        'coupon for environment',
        'couponsStar',
        z.enum(['couponsStar', 'couponsMcBookie', 'couponsDragonBet'])
    ),

    /**
     * @description - get ids europeanEliteTemplate from template mcbookie
     */
    eventsEuropeanEliteMcbookie: describe('get ids europeanEliteTemplate from template mcbookie', false, z.boolean()),

    /**
     * @description - if route is promoted-events show only eventsOnLater in mid layout
     */
    eventsOnLaterHomePage: describe(
        'if route is promoted-events show only eventsOnLater in mid layout',
        false,
        z.boolean()
    ),

    /**
     * @description Sorting mode in next off filters
     */
    filterSortingMode: describe(
        'Choose the sorting mode in filters component',
        {
            nextOff: 'name',
        },
        z.object({
            nextOff: z.enum(['name', 'startTime']),
        })
    ),

    /**
     * @description - show icons and buttons for live event stream
     */
    hasEventLiveStreaming: describe('show icons and buttons for live event stream', false, z.boolean()),

    /**
     * @description - turn on delay information in InPlayComponent
     */
    hasInPlayDelayInformation: describe('turn on delay information in InPlayComponent', false, z.boolean()),

    /**
     * @description - turn on/off top up in betslip
     */
    hasTopUpEnabled: describe('turn on/off top up in betslip', false, z.boolean()),

    /**
     * @description - turn on/off View Racecard button on Hero Widget
     */
    heroWidgetViewRacecard: describe('turn on/off View Racecard button on Hero Widget', false, z.boolean()),

    /**
     * @description order of widgets on homepage
     */
    homepageWidgetsOrder: describe('order of widgets on homepage', [], z.array(HomepageWidget)),

    /**
     * @description - add icon ios in headerMeta
     */
    isAntePostReverse: describe('add icon ios in headerMeta', false, z.boolean()),

    /**
     * @description - should promo carousel be automatic
     */
    isAutomaticCarousel: describe('should promo carousel be automatic', false, z.boolean()),

    /**
     * @description - determines whether next races region toggle is active by default
     */
    isNextRacesToggleActive: describe(
        'determines whether next races region toggle is active by default',
        false,
        z.boolean()
    ),

    /**
     * @description - decide if stream component on Special Event should be controlled by time
     */
    isStreamControlledByTime: describe(
        'decide if stream component on Special Event should be controlled by time',
        false,
        z.boolean()
    ),

    /**
     * @description - decide if stream component should be visible for unauthorized users
     */
    isStreamVisibleForUnauthorized: describe(
        'decide if stream component should be visible for unauthorized users',
        false,
        z.boolean()
    ),

    /**
     * @description - live tracking scoreboard configuration - provider name and script src
     */
    liveTracker: describe(
        'live tracking scoreboard configuration - provider name and script src',
        null,
        z
            .object({
                providerName: z.string(),
                scriptSrcUrl: z.string(),
            })
            .nullable()
    ),

    /**
     * @description - universe has no betslip
     */
    noBetslip: describe('universe has no betslip', false, z.boolean()),

    /**
     * @description - default odds for anonymous user
     */
    oddsFormatShortDefault: describe('default odds for anonymous user', 'f', z.enum(['f', 'd'])),

    /**
     * @description - -----FeaturesFlag => should be always FALSE-----
     */
    rab: describe('-----FeaturesFlag => should be always FALSE-----', false, z.boolean()),

    /**
     * @description - description in YouDecideHeader (Unavailable for cashout)
     */
    rabCashoutAvailable: describe('description in YouDecideHeader (Unavailable for cashout)', false, z.boolean()),

    /**
     * @description - Multibet widget configuration with base URL and product name
     */
    multibetWidget: describe(
        'Multibet widget configuration with base URL and product name',
        null,
        z
            .object({
                baseUrl: z.string(),
                productName: z.string(),
            })
            .nullable()
    ),

    /**
     * @description - Enable Bet Builder for InPlay events (requires multibetWidget to be configured)
     */
    betBuilderForInPlay: describe('Enable Bet Builder for InPlay events', false, z.boolean()),

    /**
     * @description - rabHeaderIcon
     */
    rabHeaderIcon: describe('rabHeaderIcon', 'BetBuilderIcon', z.enum(['BetBuilderIcon'])),
    /**
     * @description - title for rab header and bets
     */
    rabHeaderTitle: describe('title for rab header and bets', '', z.string()),
    /**
     * @description - show full "Best Odds Guaranteed" text instead of "BOG" on race card
     */
    raceCardBogFullLabel: describe(
        'show full "Best Odds Guaranteed" text instead of "BOG" on race card',
        false,
        z.boolean()
    ),
    /**
     * @description - show "Extra Places" label on race card when race has extra places market
     */
    raceCardExtraPlacesLabel: describe(
        'show "Extra Places" label on race card when race has extra places market',
        false,
        z.boolean()
    ),
    /**
     * @description - set switch between HR and GR next off
     */
    racingNextOffToggle: describe('set switch between HR and GR next off', false, z.boolean()),
    /**
     * @description - set switch between HR and GR next off
     */
    realityCheckLimitedType: describe('Has less options to pick for reality check', false, z.boolean()),
    realityCheckShowInfoMessage: describe('Shows info messages in reality check popup', false, z.boolean()),

    /**
     * @description - array of countries that RAS gold should be enabled for (current supported horse racing countries: 'US', 'GB', 'GB-ENG', 'GB-SCT', 'IE', 'AU', 'ZA')
     */
    rasGoldCountries: describe('array of countries that RAS gold should be enabled for', [], z.array(z.string())),

    /**
     * @description - decide if user has to place a bet to enable stream
     */
    shouldPlaceBetToWatchStream: describe('decide if user has to place a bet to enable stream', false, z.boolean()),

    /**
     * @description - show apple pay banner on deposit page
     */
    showApplePayBanner: describe('show apple pay banner on deposit page', false, z.boolean()),

    /**
     * @description - show sort dropdown on Horse Racing Racecard
     */
    showHorseRacingSortDropdown: describe('show sort dropdown on Horse Racing Racecard', false, z.boolean()),

    /**
     * @description - turn on slice name in race label
     */
    sliceNameRaceLabel: describe('turn on slice name in race label', false, z.boolean()),

    /**
     * @description - decide if GR sport meetings are splitted by country
     */
    sportGreyhoundMeetingsSplitByCountry: describe(
        'decide if GR sport meetings are splitted by country',
        false,
        z.boolean()
    ),

    /**
     * @description - decide if HR sport meetings are splitted by country
     */
    sportHorseracingMeetingsSplitByCountry: describe(
        'decide if HR sport meetings are splitted by country',
        false,
        z.boolean()
    ),

    /**
     * @description - decide if sports in LHS will have capitised names
     */
    sportsNamesCapitalized: describe('decide if sports in LHS will have capitised names', false, z.boolean()),

    /**
     * @description - show starting soon events on homepage
     */
    startingSoonEvents: describe('show starting soon events on homepage', false, z.boolean()),

    /**
     * @description - enable Trap Challenge
     */
    trapChallenge: describe('enable Trap Challenge', false, z.boolean()),

    /**
     * @description - show top events at home page instead of promoted events
     */
    topEvents: describe('show top events at home page instead of promoted events', false, z.boolean()),

    /**
     * @description - universeSetup
     */
    universeSetup: describe('universeSetup', [], z.array(MatchTypeZod)),

    /**
     * @description - determines whether to use new dropdown that displays net deposits in withdrawals
     */
    withdrawDropdownNew: describe(
        'determines whether to use new dropdown that displays net deposits in withdrawals',
        false,
        z.boolean()
    ),

    /**
     * @description - determines whether to validate the wallet address in BTC
     */
    withdrawValidateBtcAddress: describe(
        'determines whether to validate the wallet address in BTC',
        false,
        z.boolean()
    ),
    /**
     * @description - determines whether to use show xtreme push banner on home and casino page
     */
    xtremePush: describe(
        'determines whether to use show xtreme push banner on home and casino page',
        false,
        z.boolean()
    ),
    xtremePushGames: describe(
        'defines the prod game urls and linking promo carousel ids',
        null,
        z
            .object({
                casino: z.object({ url: z.string() }),
                sportsbook: z.object({ url: z.string() }),
            })
            .nullable()
    ),

    /**
     * @description - unified MobX events table (UnifiedSportEventsListState shadow) on sport/competition pages — non-football; excludes horseracing / greyhoundracing via routing guards
     */
    unifiedSportEventsTable: describe('unified events table (multi-sport path)', false, z.boolean()),

    /**
     * @description - show Genius football phase / match clock timers on unified in-play event rows
     */
    geniusTimersEnabled: describe(
        'show Genius football phase / match clock timers on unified in-play event rows',
        false,
        z.boolean()
    ),

    resourceSSR: describe('experimental for Resource test', false, z.boolean()),
});

export type ConfigFeaturesType = z.TypeOf<typeof ConfigFeaturesZod>;

export const getJsonSchemaFromConfigFeatures = (): unknown => {
    const addRequired = (schema: unknown): unknown => {
        console.info(JSON.stringify(schema, null, 4));

        const PropertiesZod = z.object({
            properties: z.record(z.string(), z.unknown()),
        });

        const schemaProperties = PropertiesZod.parse(schema);

        console.info(Object.keys(schemaProperties.properties));
        const propKeys: Array<string> = Object.keys(schemaProperties.properties);

        if (schema === null || schema === undefined) {
            throw Error('aa');
        }

        //@ts-expect-error
        schema.required = propKeys;

        //@ts-expect-error
        schema.title = 'Tytuł schemy';

        return schema;
    };

    const jsonSchema = addRequired(zodToJsonSchema(ConfigFeaturesZod));

    return jsonSchema;
};
